import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { Button, Input, Space, Spin, Table, Tag, Card, Tooltip, Empty, Progress, message } from 'antd'
import {
    SearchOutlined,
    PlusOutlined,
    TrophyOutlined,
    BookOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
    FileTextOutlined,
    LockOutlined,
    CalendarOutlined,
    EyeOutlined,
    UploadOutlined,
    FileExcelOutlined
} from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import dayjs from 'dayjs'

import './style.scss'

import { getCouseAssestmentsByRole, exportCourseAssessmentsDetails } from '../../../../../Constants/Api/functions/function'
import AddAssessment from '../../../Assessments/AddAssessment'
import AcademicTranscriptModal from '../../../Assessments/AcademicTranscriptModal'
import {
    getGradeDetails,
    calculateCoursePerformance,
    calculateCourseAssessmentBreakdown
} from '../../../../../Utils/gradeUtils'

const CourseAssessments = ({ courseId, course }) => {
    const [loading, setLoading] = useState(false)
    const [isExporting, setIsExporting] = useState(false)
    const [assessments, setAssessments] = useState([])
    const userProfile = useSelector(state => state.userProfile.userProfile)
    const [searchText, setSearchText] = useState('')
    const [searchedColumn, setSearchedColumn] = useState('')
    const [searchQuery, setSearchQuery] = useState('')
    const [openAddAssessment, setOpenAddAssessment] = useState(false)
    const [isTranscriptOpen, setIsTranscriptOpen] = useState(false)
    const searchInput = useRef(null)

    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth <= 768
        }
        return false
    })

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768)
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const allAssessments = useSelector(state => state.allAssessments?.allAssessments)
    const isStudent = userProfile?.role === 'student'

    const canManageAssessments =
        userProfile?.role === 'superadmin' ||
        userProfile?.role === 'admin' ||
        (userProfile?.role === 'coordinator' && ((course?.coordinatorIds && course.coordinatorIds.includes(userProfile?.id)) || String(course?.coordinatorId) === String(userProfile?.id))) ||
        (userProfile?.role === 'instructor' && ((course?.instructorIds && course.instructorIds.includes(userProfile?.id)) || String(course?.instructorId) === String(userProfile?.id)))

    // Pre-populate from Redux if available
    useEffect(() => {
        if (allAssessments && allAssessments.length > 0 && courseId) {
            const matching = allAssessments.filter(a => String(a.courseId) === String(courseId))
            if (matching.length > 0) {
                setAssessments(matching)
            }
        }
    }, [allAssessments, courseId])

    useEffect(() => {
        if (courseId && userProfile?.role && userProfile?.id) {
            handleGetAssessments()
        }
    }, [courseId, userProfile?.role, userProfile?.id])

    const handleGetAssessments = async () => {
        setLoading(true)
        try {
            const response = await getCouseAssestmentsByRole(courseId, userProfile.role, userProfile.id)
            if (Array.isArray(response) && response.length > 0) {
                setAssessments(response)
            } else if (allAssessments && allAssessments.length > 0) {
                const fallback = allAssessments.filter(a => String(a.courseId) === String(courseId))
                setAssessments(fallback)
            } else {
                setAssessments([])
            }
        } catch (error) {
            console.error('handleGetAssessments error:', error)
            if (allAssessments && allAssessments.length > 0) {
                const fallback = allAssessments.filter(a => String(a.courseId) === String(courseId))
                setAssessments(fallback)
            } else {
                setAssessments([])
            }
        }
        setLoading(false)
    }

    // Consolidated courseGroup object matching the Academic Transcript specification
    const courseGroup = useMemo(() => {
        const cName = course?.name || (assessments.length > 0 && assessments[0]?.courseName) || 'Course'
        const cCode = course?.courseid || (assessments.length > 0 && assessments[0]?.courseCode) || ''
        const cStatus = course?.status || (assessments.length > 0 && assessments[0]?.courseStatus) || 'ongoing'
        const performance = calculateCoursePerformance(assessments || [])
        const breakdown = calculateCourseAssessmentBreakdown(assessments || [])

        return {
            courseId,
            courseName: cName,
            courseCode: cCode,
            courseStatus: cStatus,
            courseObj: course,
            assessments: assessments || [],
            performance,
            breakdown,
        }
    }, [assessments, course, courseId])

    // Partition assessments into Continuous Assessments (CA) and Final Examination
    const caAssessments = useMemo(() => {
        return (assessments || []).filter(a => a.assessmentType !== 'FINAL_EXAM')
    }, [assessments])

    const finalExamAssessments = useMemo(() => {
        return (assessments || []).filter(a => a.assessmentType === 'FINAL_EXAM')
    }, [assessments])

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm()
        setSearchText(selectedKeys[0])
        setSearchedColumn(dataIndex)
    }

    const handleReset = (clearFilters) => {
        clearFilters()
        setSearchText('')
    }

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => close()}
                    >
                        Close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100)
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    })

    const renderDueDate = (endDate) => {
        if (!endDate) return <span style={{ color: '#94a3b8' }}>N/A</span>
        const now = new Date()
        const end = new Date(endDate)
        const diff = end - now
        const diffDays = Math.ceil(diff / (1000 * 60 * 60 * 24))
        let color = '#334155'
        if (diffDays <= 7 && diffDays >= 0) {
            color = '#2563eb'
        } else if (diffDays < 0) {
            color = '#dc2626'
        }
        return (
            <span style={{ color: color, fontWeight: diffDays < 0 ? 500 : 400 }}>
                {endDate}
            </span>
        )
    }

    const renderMobileAssessmentCard = (item, sectionType) => {
        const isCa = sectionType === 'CA' || item.assessmentType !== 'FINAL_EXAM'
        const isFinal = sectionType === 'FINAL_EXAM' || item.assessmentType === 'FINAL_EXAM'
        const totalSubs = Number(item.totalSubmissions) || 0
        const enrolled = Number(item.enrolledStudents) || 0
        const pct = enrolled > 0 ? Math.round((totalSubs / enrolled) * 100) : 0

        return (
            <div
                key={item.id}
                className={`mobile-assessment-card ${isFinal ? 'final-exam-card' : 'ca-card'}`}
            >
                <div className="mobile-card-header">
                    <div className="mobile-card-title-wrap">
                        <h4 className="mobile-card-title">
                            <Link to={`/dashboard/assessments/${item.id}`}>
                                {item.title}
                            </Link>
                        </h4>
                        <Space size={6} wrap style={{ marginTop: 4 }}>
                            <Tag color={isFinal ? 'purple' : 'blue'} style={{ fontSize: 11, fontWeight: 600, margin: 0, borderRadius: 4 }}>
                                {isFinal ? 'Final Exam (50%)' : 'Continuous Assessment (50%)'}
                            </Tag>
                        </Space>
                    </div>

                    <div className="mobile-card-badge">
                        {isStudent ? (
                            item.status ? (
                                <Tag color="success" icon={<CheckCircleOutlined />} style={{ margin: 0, fontWeight: 600, borderRadius: 6 }}>
                                    Attempted
                                </Tag>
                            ) : isFinal && !item.isEligibleForFinalExam ? (
                                <Tag color="error" icon={<LockOutlined />} style={{ margin: 0, fontWeight: 600, borderRadius: 6 }}>
                                    Locked
                                </Tag>
                            ) : (
                                <Tag color="default" style={{ margin: 0, fontWeight: 600, borderRadius: 6 }}>
                                    Not Attempted
                                </Tag>
                            )
                        ) : (
                            <Tag
                                color={pct >= 100 ? 'success' : pct > 0 ? 'processing' : 'default'}
                                style={{ margin: 0, fontWeight: 600, fontSize: 12, borderRadius: 6 }}
                            >
                                {pct}% Done
                            </Tag>
                        )}
                    </div>
                </div>

                <div className="mobile-card-body">
                    <div className="mobile-card-row">
                        <span className="row-label">
                            <CalendarOutlined style={{ marginRight: 6, color: '#64748b' }} />
                            Due Date:
                        </span>
                        <span className="row-value">{renderDueDate(item.endDate)}</span>
                    </div>

                    {isStudent ? (
                        <>
                            {isCa ? (
                                <div className="mobile-card-row">
                                    <span className="row-label">Marks & Result:</span>
                                    <span className="row-value">
                                        {item.marks !== null && item.marks !== undefined && item.marks !== '' && !isNaN(Number(item.marks)) ? (
                                            <Space size={6} wrap>
                                                <strong style={{ color: '#0284c7', fontSize: 14 }}>{Number(item.marks)}%</strong>
                                                <Tag color={getGradeDetails(item.marks).tagColor} style={{ fontWeight: 600, margin: 0, borderRadius: 4 }}>
                                                    Grade {getGradeDetails(item.marks).grade} • {getGradeDetails(item.marks).classification}
                                                </Tag>
                                            </Space>
                                        ) : (
                                            <span style={{ color: '#94a3b8' }}>{item.status ? 'Pending Evaluation' : 'Not Attempted'}</span>
                                        )}
                                    </span>
                                </div>
                            ) : (
                                <div className="mobile-card-row">
                                    <span className="row-label">Final Evaluation:</span>
                                    <span className="row-value">
                                        {isFinal && !item.isEligibleForFinalExam && !item.status ? (
                                            <span style={{ color: '#ef4444', fontWeight: 500, fontSize: 12 }}>
                                                Locked (Requires CA Average ≥ 50%)
                                            </span>
                                        ) : item.marks !== null && item.marks !== undefined && item.marks !== '' ? (
                                            <Tag color={getGradeDetails(item.marks).tagColor} style={{ fontWeight: 600, margin: 0 }}>
                                                Grade {getGradeDetails(item.marks).grade} • {getGradeDetails(item.marks).classification}
                                            </Tag>
                                        ) : (
                                            <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                                                {item.status ? 'Pending Evaluation' : 'Ungraded'}
                                            </span>
                                        )}
                                    </span>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="mobile-submissions-box">
                            <div className="submissions-box-header">
                                <span className="box-title">Student Submissions</span>
                                <span className="box-count">
                                    <strong>{totalSubs}</strong> / {enrolled} Enrolled
                                </span>
                            </div>
                            <Progress
                                percent={Math.min(pct, 100)}
                                showInfo={false}
                                size="small"
                                strokeColor={pct >= 80 ? '#10b981' : pct >= 40 ? '#3b82f6' : '#f59e0b'}
                                trailColor="#e2e8f0"
                                style={{ margin: '6px 0 0 0' }}
                            />
                        </div>
                    )}
                </div>

                <div className="mobile-card-footer">
                    {isStudent ? (
                        item.status ? (
                            <Link to={`/dashboard/assessments/${item.id}`} style={{ width: '100%', display: 'block' }}>
                                <Button block icon={<EyeOutlined />} style={{ fontWeight: 600, height: 38, borderRadius: 8 }}>
                                    View My Submission
                                </Button>
                            </Link>
                        ) : isFinal && !item.isEligibleForFinalExam ? (
                            <Tooltip title={item.caEligibilityReason || 'Requires Continuous Assessment Average ≥ 50%'}>
                                <Button
                                    block
                                    disabled
                                    danger={item.caEligibilityStatus === 'NOT_ELIGIBLE'}
                                    style={{ fontWeight: 600, height: 38, borderRadius: 8 }}
                                >
                                    <LockOutlined /> {item.caEligibilityStatus === 'NOT_ELIGIBLE' ? 'Ineligible for Final Exam' : 'Final Exam Locked (CA < 50%)'}
                                </Button>
                            </Tooltip>
                        ) : isFinal ? (
                            <Link to={`/dashboard/assessments/${item.id}`} style={{ width: '100%', display: 'block' }}>
                                <Button
                                    block
                                    type="primary"
                                    icon={<UploadOutlined />}
                                    style={{ fontWeight: 600, height: 38, borderRadius: 8, background: '#8b5cf6', borderColor: '#8b5cf6' }}
                                >
                                    Submit Final Exam →
                                </Button>
                            </Link>
                        ) : (
                            <Link to={`/dashboard/assessments/${item.id}`} style={{ width: '100%', display: 'block' }}>
                                <Button block type="primary" icon={<UploadOutlined />} style={{ fontWeight: 600, height: 38, borderRadius: 8 }}>
                                    Click to Submit Your Assignment →
                                </Button>
                            </Link>
                        )
                    ) : (
                        <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                            <Link to={`/dashboard/assessments/${item.id}`} style={{ flex: 1 }}>
                                <Button block type="primary" icon={<FileTextOutlined />} style={{ fontWeight: 600, height: 38, borderRadius: 8 }}>
                                    View Submissions ({totalSubs}) →
                                </Button>
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    // 1. Continuous Assessments (CA) Columns for Students (shows Marks & Grade)
    const studentCAColumns = [
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            width: '14%',
            render: (text, record) => {
                if (record.status) {
                    return (
                        <Link to={`/dashboard/assessments/${record.id}`}>
                            <Button type="link" size="small" style={{ padding: 0 }}>
                                View Submission
                            </Button>
                        </Link>
                    )
                }
                return (
                    <Link to={`/dashboard/assessments/${record.id}`}>
                        <Button type="link" size="small" style={{ padding: 0, fontWeight: 600 }}>
                            Submit Task
                        </Button>
                    </Link>
                )
            },
        },
        {
            title: 'Continuous Assessment Task',
            dataIndex: 'title',
            key: 'title',
            width: '38%',
            ...getColumnSearchProps('title'),
            render: (title) => <span style={{ fontWeight: 600, color: '#0f172a' }}>{title}</span>
        },
        {
            title: 'Due Date',
            dataIndex: 'endDate',
            key: 'endDate',
            width: '16%',
            sorter: (a, b) => new Date(a.endDate) - new Date(b.endDate),
            render: renderDueDate,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: '12%',
            render: (status) => status ? (
                <Tag color="success">Attempted</Tag>
            ) : (
                <Tag color="default">Not Attempted</Tag>
            )
        },
        {
            title: 'Marks (%)',
            dataIndex: 'marks',
            key: 'marks',
            width: '10%',
            align: 'right',
            render: (marks, record) => {
                if (marks !== null && marks !== undefined && marks !== '' && !isNaN(Number(marks))) {
                    return <strong style={{ color: '#0284c7', fontSize: 14 }}>{Number(marks)}%</strong>
                }
                return <span style={{ color: '#94a3b8' }}>{record.status ? 'Pending' : '-'}</span>
            }
        },
        {
            title: 'Grade Awarded',
            dataIndex: 'marks',
            key: 'grade',
            width: '18%',
            render: (marks, record) => {
                if (marks === null || marks === undefined || marks === '') {
                    return (
                        <span style={{ color: '#8c8c8c', fontSize: 13 }}>
                            {record.status ? 'Pending Evaluation' : 'Ungraded'}
                        </span>
                    )
                }
                const gradeInfo = getGradeDetails(marks)
                return (
                    <Tag color={gradeInfo.tagColor} style={{ fontWeight: '600' }}>
                        Grade {gradeInfo.grade} • {gradeInfo.classification}
                    </Tag>
                )
            }
        }
    ]

    // 2. Final Examination Columns for Students (strictly NO numeric marks)
    const studentFinalExamColumns = [
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            width: '16%',
            render: (text, record) => {
                if (record.status) {
                    return (
                        <Link to={`/dashboard/assessments/${record.id}`}>
                            <Button type="link" size="small" style={{ padding: 0 }}>
                                View Submission
                            </Button>
                        </Link>
                    )
                }

                if (!record.isEligibleForFinalExam) {
                    if (record.caEligibilityStatus === 'NOT_ELIGIBLE') {
                        return (
                            <Tooltip title={record.caEligibilityReason || 'Ineligible for Final Exam (CA average < 50%)'}>
                                <Button size="small" disabled danger style={{ padding: '0 8px', borderRadius: 4 }}>
                                    <LockOutlined /> Ineligible
                                </Button>
                            </Tooltip>
                        )
                    }
                    return (
                        <Tooltip title={record.caEligibilityReason || 'Awaiting evaluation of all Continuous Assessments'}>
                            <Button size="small" disabled style={{ padding: '0 8px', borderRadius: 4 }}>
                                <LockOutlined /> Locked
                            </Button>
                        </Tooltip>
                    )
                }

                return (
                    <Link to={`/dashboard/assessments/${record.id}`}>
                        <Button type="primary" size="small" style={{ padding: '0 10px', borderRadius: 4 }}>
                            Submit Final Exam
                        </Button>
                    </Link>
                )
            },
        },
        {
            title: 'Final Examination Component',
            dataIndex: 'title',
            key: 'title',
            width: '46%',
            ...getColumnSearchProps('title'),
            render: (title) => <span style={{ fontWeight: 600, color: '#0f172a' }}>{title}</span>
        },
        {
            title: 'Due Date',
            dataIndex: 'endDate',
            key: 'endDate',
            width: '18%',
            sorter: (a, b) => new Date(a.endDate) - new Date(b.endDate),
            render: renderDueDate,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: '12%',
            render: (status, record) => {
                if (status) {
                    return <Tag color="success">Attempted</Tag>
                }
                if (!record.isEligibleForFinalExam) {
                    return <Tag color="error">Ineligible</Tag>
                }
                return <Tag color="default">Not Attempted</Tag>
            }
        },
        {
            title: 'Grade Awarded',
            dataIndex: 'marks',
            key: 'grade',
            width: '18%',
            render: (marks, record) => {
                if (!record.isEligibleForFinalExam && !record.status) {
                    return <span style={{ color: '#ef4444', fontWeight: 500 }}>Ineligible</span>
                }
                if (marks === null || marks === undefined || marks === '') {
                    return (
                        <span style={{ color: '#8c8c8c', fontSize: 13 }}>
                            {record.status ? 'Pending Evaluation' : 'Ungraded'}
                        </span>
                    )
                }
                const gradeInfo = getGradeDetails(marks)
                return (
                    <Tag color={gradeInfo.tagColor} style={{ fontWeight: '600' }}>
                        Grade {gradeInfo.grade} • {gradeInfo.classification}
                    </Tag>
                )
            }
        }
    ]

    // Admin Columns for CA Tasks
    const adminCAColumns = [
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            width: '14%',
            render: (text, record) => (
                <Space size="middle">
                    <Link to={`/dashboard/assessments/${record.id}`}>
                        <Button type="link" size="small" style={{ padding: 0, fontWeight: 600 }}>
                            View Task
                        </Button>
                    </Link>
                </Space>
            ),
        },
        {
            title: 'Continuous Assessment Task',
            dataIndex: 'title',
            key: 'title',
            width: '44%',
            ...getColumnSearchProps('title'),
            render: (title) => <span style={{ fontWeight: 600, color: '#0f172a' }}>{title}</span>
        },
        {
            title: 'Due Date',
            dataIndex: 'endDate',
            key: 'endDate',
            width: '20%',
            sorter: (a, b) => new Date(a.endDate) - new Date(b.endDate),
            render: renderDueDate,
        },
        {
            title: 'Submissions',
            dataIndex: 'totalSubmissions',
            key: 'totalSubmissions',
            width: '24%',
            sorter: (a, b) => (Number(a.totalSubmissions) || 0) - (Number(b.totalSubmissions) || 0),
            render: (val, record) => {
                const totalSubs = Number(record.totalSubmissions) || 0;
                const enrolled = Number(record.enrolledStudents) || 0;
                const pct = enrolled > 0 ? Math.round((totalSubs / enrolled) * 100) : 0;
                return (
                    <div style={{ minWidth: 120 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                            <Link to={`/dashboard/assessments/${record.id}`}>
                                <span style={{ fontWeight: 700, color: '#0f172a', fontSize: 13 }}>
                                    {totalSubs}{' '}
                                    <span style={{ color: '#64748b', fontWeight: 500, fontSize: 12 }}>
                                        / {enrolled} Enrolled
                                    </span>
                                </span>
                            </Link>
                            <Tag color={pct >= 100 ? 'success' : pct > 0 ? 'processing' : 'default'} style={{ margin: 0, fontSize: 11, padding: '0 5px', borderRadius: 8 }}>
                                {pct}%
                            </Tag>
                        </div>
                        <Progress
                            percent={Math.min(pct, 100)}
                            showInfo={false}
                            size="small"
                            strokeColor={pct >= 80 ? '#16a34a' : pct >= 40 ? '#2563eb' : '#f59e0b'}
                            trailColor="#f1f5f9"
                            style={{ margin: 0 }}
                        />
                    </div>
                );
            }
        }
    ]

    // Admin Columns for Final Exam
    const adminFinalExamColumns = [
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            width: '14%',
            render: (text, record) => (
                <Space size="middle">
                    <Link to={`/dashboard/assessments/${record.id}`}>
                        <Button type="link" size="small" style={{ padding: 0, fontWeight: 600 }}>
                            View Task
                        </Button>
                    </Link>
                </Space>
            ),
        },
        {
            title: 'Final Examination Component',
            dataIndex: 'title',
            key: 'title',
            width: '44%',
            ...getColumnSearchProps('title'),
            render: (title) => <span style={{ fontWeight: 600, color: '#0f172a' }}>{title}</span>
        },
        {
            title: 'Due Date',
            dataIndex: 'endDate',
            key: 'endDate',
            width: '20%',
            sorter: (a, b) => new Date(a.endDate) - new Date(b.endDate),
            render: renderDueDate,
        },
        {
            title: 'Submissions',
            dataIndex: 'totalSubmissions',
            key: 'totalSubmissions',
            width: '24%',
            sorter: (a, b) => (Number(a.totalSubmissions) || 0) - (Number(b.totalSubmissions) || 0),
            render: (val, record) => {
                const totalSubs = Number(record.totalSubmissions) || 0;
                const enrolled = Number(record.enrolledStudents) || 0;
                const pct = enrolled > 0 ? Math.round((totalSubs / enrolled) * 100) : 0;
                return (
                    <div style={{ minWidth: 120 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                            <Link to={`/dashboard/assessments/${record.id}`}>
                                <span style={{ fontWeight: 700, color: '#0f172a', fontSize: 13 }}>
                                    {totalSubs}{' '}
                                    <span style={{ color: '#64748b', fontWeight: 500, fontSize: 12 }}>
                                        / {enrolled} Enrolled
                                    </span>
                                </span>
                            </Link>
                            <Tag color={pct >= 100 ? 'success' : pct > 0 ? 'processing' : 'default'} style={{ margin: 0, fontSize: 11, padding: '0 5px', borderRadius: 8 }}>
                                {pct}%
                            </Tag>
                        </div>
                        <Progress
                            percent={Math.min(pct, 100)}
                            showInfo={false}
                            size="small"
                            strokeColor={pct >= 80 ? '#16a34a' : pct >= 40 ? '#2563eb' : '#f59e0b'}
                            trailColor="#f1f5f9"
                            style={{ margin: 0 }}
                        />
                    </div>
                );
            }
        }
    ]

    // Prepared CA Data
    const caTableData = useMemo(() => {
        let list = caAssessments.map(a => ({
            id: a.id,
            key: a.id,
            title: a.name,
            assessmentType: 'CA',
            endDate: a.endDate,
            status: a.attempt?.id ? true : false,
            marks: a.attempt?.marks,
            totalSubmissions: Number(a.totalSubmissions) || 0,
            uniqueSubmissions: Number(a.uniqueSubmissions) || 0,
            enrolledStudents: Number(a.enrolledStudents) || 0,
            rawAssessment: a,
        }))
        const q = (searchQuery || '').trim().toLowerCase()
        if (q) {
            list = list.filter(item => (item.title || '').toLowerCase().includes(q))
        }
        return list
    }, [caAssessments, searchQuery])

    // Prepared Final Exam Data
    const finalExamTableData = useMemo(() => {
        let list = finalExamAssessments.map(a => ({
            id: a.id,
            key: a.id,
            title: a.name,
            assessmentType: 'FINAL_EXAM',
            endDate: a.endDate,
            status: a.attempt?.id ? true : false,
            marks: a.attempt?.marks,
            totalSubmissions: Number(a.totalSubmissions) || 0,
            uniqueSubmissions: Number(a.uniqueSubmissions) || 0,
            enrolledStudents: Number(a.enrolledStudents) || 0,
            isEligibleForFinalExam: courseGroup?.breakdown?.caEligibility?.isEligible,
            caEligibilityStatus: courseGroup?.breakdown?.caEligibility?.status,
            caEligibilityReason: courseGroup?.breakdown?.caEligibility?.reason,
            rawAssessment: a,
        }))
        const q = (searchQuery || '').trim().toLowerCase()
        if (q) {
            list = list.filter(item => (item.title || '').toLowerCase().includes(q))
        }
        return list
    }, [finalExamAssessments, courseGroup, searchQuery])

    const breakdown = courseGroup?.breakdown

    const handleExportAssessmentsToExcel = async () => {
        try {
            setIsExporting(true)
            message.loading({ content: 'Preparing course assessment and certificate details...', key: 'exportAssessmentsMsg' })

            const res = await exportCourseAssessmentsDetails(courseId)
            if (!res || !res.success || !Array.isArray(res.data) || res.data.length === 0) {
                message.warning({
                    content: res?.message || 'No enrolled student records found to export for this course.',
                    key: 'exportAssessmentsMsg',
                })
                return
            }

            const courseAssessments = Array.isArray(res.courseAssessments) ? res.courseAssessments : []
            const totalCAs = courseAssessments.filter(a => a.assessmentType !== 'FINAL_EXAM').length
            const totalFinals = courseAssessments.filter(a => a.assessmentType === 'FINAL_EXAM').length

            // Map into comprehensive columns including all individual assessment marks & grades
            const excelRows = res.data.map((item, index) => {
                const row = {
                    'No.': index + 1,
                    'Name to Appear on Certificate': item.nameToAppearOnCertificate || '—',
                    'Student Registration Number': item.studentRegistrationNumber || '—',
                    'Course Name': item.courseName || courseGroup?.courseName || '—',
                }

                // 1. All Individual Course Assessments (CAs & Final Exam)
                let caCount = 1
                let finalCount = 1
                courseAssessments.forEach((ass) => {
                    const isFinal = ass.assessmentType === 'FINAL_EXAM'
                    const prefix = isFinal
                        ? (totalFinals > 1 ? `Final Exam ${finalCount++}` : 'Final Exam')
                        : (totalCAs > 1 ? `CA ${caCount++}` : 'CA')
                    const assName = ass.name || `Assessment`
                    const assData = item.individualAssessments?.[ass.id] || {}

                    row[`${prefix}: ${assName} - Marks`] = assData.marks || '—'
                    row[`${prefix}: ${assName} - Grade`] = assData.displayGrade || '—'
                })

                // 2. Continuous Assessments (CA) Average (if course has CAs)
                if (res.hasCA) {
                    row['CA Average Marks'] = item.caAverageMarks || '—'
                    row['CA Average Grade'] = item.caGrade || '—'
                }

                // 3. Final Examination Summary (if course has Final Exam)
                if (res.hasFinalExam) {
                    row['Final Exam Marks'] = item.finalExamMarks || '—'
                    row['Final Exam Grade'] = item.finalExamGrade || '—'
                }

                // 4. Overall Final Certificate Standing
                row['Certificate Marks'] = item.certificateMarks || '—'
                row['Grade'] = item.grade || '—'
                row['Classification'] = item.classification || '—'

                // 5. Student Profile & Certificate Delivery Logistics
                row['Certificate Number'] = item.certificateNumber || '—'
                row['Permanent Address'] = item.permanentAddress || '—'
                row['Certificate Delivery Address'] = item.certificateDeliveryAddress || '—'
                row['Nearest City / Town'] = item.nearestCityTown || '—'
                row['Contact Number'] = item.contactNumber || '—'
                row['WhatsApp Number'] = item.whatsappNumber || '—'
                row['Email Address'] = item.emailAddress || '—'
                row['Certificate Issue Date'] = item.certificateIssueDate || '—'
                row['Certificate Status'] = item.certificateStatus || '—'
                row['Courier Tracking Number'] = item.courierTrackingNumber || '—'

                return row
            })

            const xlsx = await import('xlsx')
            const FileSaver = await import('file-saver')

            const worksheet = xlsx.utils.json_to_sheet(excelRows)

            // Auto-size column widths
            const colWidths = Object.keys(excelRows[0] || {}).map((key) => {
                let maxLen = key.length
                excelRows.forEach((r) => {
                    const val = r[key] ? String(r[key]) : ''
                    if (val.length > maxLen) maxLen = val.length
                })
                return { wch: Math.min(Math.max(maxLen + 3, 14), 48) }
            })
            worksheet['!cols'] = colWidths

            // Grading Rubric Reference Sheet
            const rubricRows = [
                { 'Marks Range': '85% - 100%', 'Grade': 'A', 'Classification': 'Distinction Pass', 'Performance Standard': 'Outstanding Mastery & Distinction Pass' },
                { 'Marks Range': '75% - 84%',  'Grade': 'B', 'Classification': 'Merit Pass',       'Performance Standard': 'Commendable Mastery & Merit Pass' },
                { 'Marks Range': '65% - 74%',  'Grade': 'C', 'Classification': 'Credit Pass',      'Performance Standard': 'Good Competence & Credit Pass' },
                { 'Marks Range': '50% - 64%',  'Grade': 'S', 'Classification': 'Ordinary Pass',    'Performance Standard': 'Minimum Pass & Satisfactory Competence' },
                { 'Marks Range': '0% - 49%',   'Grade': 'F', 'Classification': 'Not Qualified',    'Performance Standard': 'Unsatisfactory / Incomplete Assessment' },
            ]
            const rubricWorksheet = xlsx.utils.json_to_sheet(rubricRows)
            rubricWorksheet['!cols'] = [{ wch: 18 }, { wch: 10 }, { wch: 20 }, { wch: 45 }]

            const courseNameClean = (res.course?.name || courseGroup?.courseName || 'Course')
                .replace(/[^a-zA-Z0-9_\- ]/g, '')
                .trim()
                .replace(/\s+/g, '_')

            const dateStr = dayjs().format('YYYY-MM-DD')
            const workbook = {
                Sheets: {
                    'Assessments & Delivery': worksheet,
                    'Grading Scale Rubric': rubricWorksheet,
                },
                SheetNames: ['Assessments & Delivery', 'Grading Scale Rubric'],
            }

            const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' })
            const dataBlob = new Blob([excelBuffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
            })

            FileSaver.default.saveAs(dataBlob, `${courseNameClean}_Assessment_Details_${dateStr}.xlsx`)
            message.success({
                content: `Successfully exported ${excelRows.length} assessment records for ${res.course?.name || 'this course'}!`,
                key: 'exportAssessmentsMsg',
            })
        } catch (error) {
            console.error('handleExportAssessmentsToExcel error:', error)
            message.error({ content: 'Failed to export course assessment details. Please try again.', key: 'exportAssessmentsMsg' })
        } finally {
            setIsExporting(false)
        }
    }

    return (
        <div className="course-assessments-container">
            <Spin spinning={loading} tip="Loading Course Assessments...">
                {/* Top Bar Header */}
                <div className="course-assessments-top-bar">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <h2 className="section-title">Course Assessments</h2>
                        {assessments && assessments.length > 0 && (
                            <Tag color="blue" style={{ fontSize: 13, padding: '2px 8px', borderRadius: 4 }}>
                                {assessments.length} {assessments.length === 1 ? 'Task' : 'Tasks'} Total
                            </Tag>
                        )}
                    </div>

                    <div className="section-controls">
                        {assessments && assessments.length > 3 && (
                            <Input.Search
                                className="course-search-input"
                                placeholder="Filter tasks by title..."
                                allowClear
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ width: 220 }}
                                size="middle"
                            />
                        )}

                        {canManageAssessments && (
                            <>
                                <Button
                                    icon={<FileExcelOutlined style={{ color: '#059669' }} />}
                                    onClick={handleExportAssessmentsToExcel}
                                    loading={isExporting}
                                    style={{
                                        borderRadius: 6,
                                        fontWeight: 600,
                                        color: '#065f46',
                                        backgroundColor: '#ecfdf5',
                                        borderColor: '#6ee7b7',
                                    }}
                                >
                                    Export Assessment Details
                                </Button>

                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setOpenAddAssessment(true)}
                                    style={{ borderRadius: 6, fontWeight: 600 }}
                                >
                                    Create Assessment
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {/* Course Group Card */}
                {assessments.length === 0 ? (
                    <Card className="course-group-card" style={{ padding: 32, textAlign: 'center' }}>
                        <Empty description="No assessments created for this course yet." />
                        {canManageAssessments && (
                            <Space wrap style={{ marginTop: 16 }}>
                                <Button
                                    icon={<FileExcelOutlined style={{ color: '#059669' }} />}
                                    onClick={handleExportAssessmentsToExcel}
                                    loading={isExporting}
                                    style={{
                                        borderRadius: 6,
                                        fontWeight: 600,
                                        color: '#065f46',
                                        backgroundColor: '#ecfdf5',
                                        borderColor: '#6ee7b7',
                                    }}
                                >
                                    Export Assessment Details
                                </Button>

                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    style={{ borderRadius: 6, fontWeight: 600 }}
                                    onClick={() => setOpenAddAssessment(true)}
                                >
                                    Create First Assessment
                                </Button>
                            </Space>
                        )}
                    </Card>
                ) : (
                    <Card className="course-group-card" bodyStyle={{ padding: 0 }}>
                        {/* Course Card Header */}
                        <div className="course-group-header">
                            <div className="course-info-wrap">
                                <div className="course-icon-box">
                                    <BookOutlined style={{ fontSize: 22 }} />
                                </div>
                                <div className="course-title-box">
                                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#0284c7', marginBottom: 2 }}>
                                        Course Assessments
                                    </div>
                                    <h2>
                                        {courseGroup?.courseName || course?.name || 'Course'}
                                    </h2>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                                        {courseGroup?.courseCode && (
                                            <Tag color="geekblue" style={{ fontWeight: 600 }}>
                                                {courseGroup.courseCode}
                                            </Tag>
                                        )}
                                        <Tag color={courseGroup?.courseStatus === 'completed' ? 'default' : 'cyan'}>
                                            {courseGroup?.courseStatus === 'completed' ? 'Completed' : 'Ongoing'}
                                        </Tag>
                                        <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>
                                            {caAssessments.length} CA {caAssessments.length === 1 ? 'task' : 'tasks'} • {finalExamAssessments.length} Final Exam
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Standing & Transcript Button for Students */}
                            {isStudent ? (
                                <div className="course-header-standing" style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                                    <div className="standing-status-wrap" style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', fontWeight: 600 }}>
                                            Course Standing
                                        </div>
                                        {breakdown?.isFullyCompleted ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end', marginTop: 2 }}>
                                                <TrophyOutlined style={{ color: breakdown.overallColor, fontSize: 16 }} />
                                                <span style={{ fontSize: 15, fontWeight: 700, color: breakdown.overallColor }}>
                                                    {breakdown.overallClassification} (Grade {breakdown.overallGrade})
                                                </span>
                                            </div>
                                        ) : courseGroup?.performance?.isGraded ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end', marginTop: 2 }}>
                                                <TrophyOutlined style={{ color: courseGroup.performance.color, fontSize: 16 }} />
                                                <span style={{ fontSize: 14, fontWeight: 700, color: courseGroup.performance.color }}>
                                                    {courseGroup.performance.classification} (Grade {courseGroup.performance.grade})
                                                </span>
                                            </div>
                                        ) : (
                                            <Tag color="default" style={{ marginTop: 2 }}>
                                                Pending Evaluation
                                            </Tag>
                                        )}
                                    </div>

                                    <Button
                                        type="primary"
                                        className="academic-transcript-btn"
                                        size="middle"
                                        icon={<FileTextOutlined style={{ color: '#ffffff' }} />}
                                        onClick={() => setIsTranscriptOpen(true)}
                                        style={{
                                            background: '#0284c7',
                                            borderColor: '#0284c7',
                                            color: '#ffffff',
                                            borderRadius: 6,
                                            fontWeight: 600,
                                            boxShadow: '0 2px 4px rgba(2, 132, 199, 0.25)',
                                        }}
                                    >
                                        Academic Transcript
                                    </Button>
                                </div>
                            ) : (
                                canManageAssessments && (
                                    <div className="course-header-action" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <Button
                                            icon={<FileExcelOutlined style={{ color: '#059669' }} />}
                                            onClick={handleExportAssessmentsToExcel}
                                            loading={isExporting}
                                            style={{
                                                borderRadius: 6,
                                                fontWeight: 600,
                                                color: '#065f46',
                                                backgroundColor: '#ecfdf5',
                                                borderColor: '#6ee7b7',
                                            }}
                                        >
                                            Export Assessment Details
                                        </Button>
                                    </div>
                                )
                            )}
                        </div>

                        {/* ============================================================== */}
                        {/* SECTION 1: CONTINUOUS ASSESSMENTS (CA) TABLE (With Marks)     */}
                        {/* ============================================================== */}
                        <div className="assessment-sub-section">
                            <div className="sub-section-header">
                                <div className="sub-section-title-wrap" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
                                        Part 1: Continuous Assessments (CA)
                                    </span>
                                    <Tag color="blue" style={{ fontWeight: 600, borderRadius: 4 }}>
                                        Weightage: 50%
                                    </Tag>
                                    <span style={{ fontSize: 13, color: '#64748b' }}>
                                        ({caAssessments.length} {caAssessments.length === 1 ? 'task' : 'tasks'})
                                    </span>
                                </div>

                                {isStudent && (
                                    <div className="sub-section-tag-wrap" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                        {breakdown?.hasCA && (
                                            <Tooltip title={breakdown.caEligibility.reason}>
                                                <Space size={6} wrap>
                                                    <Tag color="blue" style={{ fontWeight: 600, margin: 0 }}>
                                                        CA Avg: {breakdown.caEligibility.caAverage !== null ? `${breakdown.caEligibility.caAverage}%` : 'Pending'}
                                                    </Tag>
                                                    <Tag
                                                        color={breakdown.caEligibility.tagColor}
                                                        style={{ fontWeight: 600, margin: 0, cursor: 'pointer' }}
                                                    >
                                                        {breakdown.caEligibility.status === 'ELIGIBLE' && <CheckCircleOutlined style={{ marginRight: 4 }} />}
                                                        {breakdown.caEligibility.status === 'NOT_ELIGIBLE' && <CloseCircleOutlined style={{ marginRight: 4 }} />}
                                                        {breakdown.caEligibility.status === 'PENDING' && <ClockCircleOutlined style={{ marginRight: 4 }} />}
                                                        {breakdown.caEligibility.label}
                                                    </Tag>
                                                </Space>
                                            </Tooltip>
                                        )}
                                    </div>
                                )}
                            </div>

                            {caAssessments.length === 0 ? (
                                <div style={{ padding: '20px 24px', color: '#94a3b8', fontSize: 13, textAlign: 'center' }}>
                                    No Continuous Assessment tasks assigned for this course.
                                </div>
                            ) : (
                                <>
                                    {!isMobile && (
                                        <div className="assessment-desktop-table">
                                            <Table
                                                columns={isStudent ? studentCAColumns : adminCAColumns}
                                                dataSource={caTableData}
                                                pagination={false}
                                                size="middle"
                                                scroll={{ x: isStudent ? 850 : 700 }}
                                            />
                                        </div>
                                    )}
                                    {isMobile && (
                                        <div className="assessment-mobile-cards">
                                            {caTableData.map(item => renderMobileAssessmentCard(item, 'CA'))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* ============================================================== */}
                        {/* SECTION 2: FINAL EXAMINATION TABLE (No numeric marks for st.)  */}
                        {/* ============================================================== */}
                        <div className="assessment-sub-section" style={{ borderTop: '1px solid #e2e8f0' }}>
                            <div className="sub-section-header">
                                <div className="sub-section-title-wrap" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
                                        Part 2: Final Examination
                                    </span>
                                    <Tag color="purple" style={{ fontWeight: 600, borderRadius: 4 }}>
                                        Weightage: 50%
                                    </Tag>
                                    <span style={{ fontSize: 13, color: '#64748b' }}>
                                        ({finalExamAssessments.length} {finalExamAssessments.length === 1 ? 'task' : 'tasks'})
                                    </span>
                                </div>

                                {isStudent && (
                                    <div className="sub-section-tag-wrap" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                        {breakdown?.finalExamPerformance?.isGraded ? (
                                            <Tag color={breakdown?.finalExamPerformance?.tagColor || 'gold'} style={{ fontWeight: 600, padding: '3px 10px', borderRadius: 6 }}>
                                                Awarded: Grade {breakdown.finalExamPerformance.grade} • {breakdown.finalExamPerformance.classification}
                                            </Tag>
                                        ) : !breakdown?.caEligibility?.isEligible ? (
                                            <Tag color="error" style={{ fontWeight: 600, padding: '3px 10px', borderRadius: 6 }}>
                                                <LockOutlined style={{ marginRight: 4 }} /> Final Exam Locked: Requires CA Average ≥ 50%
                                            </Tag>
                                        ) : (
                                            <Tag color="cyan" style={{ fontWeight: 600, padding: '3px 10px', borderRadius: 6 }}>
                                                <CheckCircleOutlined style={{ marginRight: 4 }} /> Eligible to Attempt Final Exam
                                            </Tag>
                                        )}
                                    </div>
                                )}
                            </div>

                            {finalExamAssessments.length === 0 ? (
                                <div style={{ padding: '20px 24px', color: '#94a3b8', fontSize: 13, textAlign: 'center' }}>
                                    No Final Examination published for this course yet.
                                </div>
                            ) : (
                                <>
                                    {!isMobile && (
                                        <div className="assessment-desktop-table">
                                            <Table
                                                columns={isStudent ? studentFinalExamColumns : adminFinalExamColumns}
                                                dataSource={finalExamTableData}
                                                pagination={false}
                                                size="middle"
                                                scroll={{ x: isStudent ? 850 : 700 }}
                                            />
                                        </div>
                                    )}
                                    {isMobile && (
                                        <div className="assessment-mobile-cards">
                                            {finalExamTableData.map(item => renderMobileAssessmentCard(item, 'FINAL_EXAM'))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Summary Calculation Footer for Students */}
                        {isStudent && breakdown && (
                            <div className="course-group-footer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <TrophyOutlined style={{ color: breakdown.overallColor || '#0284c7', fontSize: 16 }} />
                                    <span style={{ fontSize: 13, color: '#334155', fontWeight: 500 }}>
                                        {breakdown.summaryText}
                                    </span>
                                </div>
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<FileTextOutlined style={{ color: '#0284c7' }} />}
                                    onClick={() => setIsTranscriptOpen(true)}
                                    style={{ fontWeight: 600, color: '#0284c7', padding: 0 }}
                                >
                                    View Full Academic Transcript & Grading Report →
                                </Button>
                            </div>
                        )}
                    </Card>
                )}
            </Spin>

            {/* Modal: Create Assessment */}
            {canManageAssessments && (
                <AddAssessment
                    isOpen={openAddAssessment}
                    setOpen={setOpenAddAssessment}
                    cources={[course].filter(Boolean)}
                    handleGetAssessments={handleGetAssessments}
                    loading={loading}
                />
            )}

            {/* Modal: Official Academic Transcript */}
            <AcademicTranscriptModal
                isOpen={isTranscriptOpen}
                onClose={() => setIsTranscriptOpen(false)}
                courseGroup={courseGroup}
                userProfile={userProfile}
            />
        </div>
    )
}

export default CourseAssessments